"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";

type Phase = "idle" | "exiting" | "entering";

const EXIT_MS = 420;
const ENTER_MS = 520;

interface TransitionValue {
  phase: Phase;
  navigate: (href: string) => void;
}

const TransitionContext = createContext<TransitionValue>({
  phase: "idle",
  navigate: () => {},
});

export const usePageTransition = () => useContext(TransitionContext);

interface PageTransitionProps {
  nav: ReactNode;
  footer: ReactNode;
  children: ReactNode;
}

export default function PageTransition({
  nav,
  footer,
  children,
}: PageTransitionProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [phase, setPhase] = useState<Phase>("idle");
  const busy = useRef(false);

  const navigate = useCallback(
    (href: string) => {
      if (busy.current || href === pathname) return;

      // Уважаем системную настройку — без анимации сразу переходим
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      if (reduceMotion) {
        router.push(href);
        return;
      }

      busy.current = true;
      setPhase("exiting");

      window.setTimeout(() => {
        router.push(href);
        setPhase("entering");
        window.setTimeout(() => {
          setPhase("idle");
          busy.current = false;
        }, ENTER_MS);
      }, EXIT_MS);
    },
    [pathname, router]
  );

  const cls =
    phase === "exiting"
      ? " is-exiting"
      : phase === "entering"
        ? " is-entering"
        : "";

  return (
    <TransitionContext.Provider value={{ phase, navigate }}>
      {nav}
      <main className={`t-content${cls}`}>{children}</main>
      <div className={`t-footer${cls}`}>{footer}</div>
    </TransitionContext.Provider>
  );
}
